// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/UploadPage.tsx (ADR-0005) —
// REBUILT on the estate tokens (ADR-0010). Everything behavioural carries: the per-side
// session blanks, XHR progress with an honest ETA, the encrypted-file password retry (the
// password is used to open the file and never stored), the sha-dedupe note, the grain
// question asked ONCE, the tolerance that never silently becomes the period's default, run
// adoption only once a run this session started COMPLETES, and delete refusing on frozen
// provenance (the 409 comes from the server; the UI renders its sentence).
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  Check,
  Download,
  FileSpreadsheet,
  Lock,
  Trash2,
  Upload as UploadIcon,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { api, postJson } from '../../api/client'
import { usePeriodSummary, useRun, useRuns, useUploads } from '../../api/hooks'
import type { ParseStats, Run, Upload } from '../../api/types'
import { uploadWithProgress, type UploadProgress } from '../../api/upload'
import { dedupeNote, periodCue } from '../../api/uploadNotes'
import { probeFailureSentence, uploadTone } from '../../api/uploadTone'
import { CarriedCorrectionsNote, CarriedVerdictsNote } from '../../components/CarriedNotes'
import { InfoTip } from '../../components/InfoTip'
import { MappingModal } from '../../components/MappingModal'
import { Btn, Modal } from '../../components/Modal'
import { Money } from '../../components/Money'
import { ProcessingPanel } from '../../components/ProcessingPanel'
import { Chip, StatusBadge } from '../../components/badges'
import { showToast } from '../../components/toast'
import { fmtBytes, fmtDateTime, fmtInt, runTag } from '../../format'
import { useEvaluation } from '../../lib/access'
import { adoptableRun, rememberRun } from '../../workspace/runContext'
import { activeUpload, useWorkspaceSession } from '../../workspace/workspaceSession'

const KIND_META = {
  source: {
    title: 'Form 26AS (TRACES)',
    hint: 'Annual tax statement — .xlsx / .csv / .txt / .pdf / .json',
    parse: 'traces_26as',
  },
  book: {
    title: 'GL Ledger (books)',
    hint: 'TDS receivable ledger export — any format, as it is',
    parse: 'gl_ledger',
  },
} as const

type Phase = 'idle' | 'transferring' | 'parsing'

function etaText(p: UploadProgress): string {
  if (!p.rate || p.fraction >= 1) return ''
  const secs = Math.max(0, (p.total - p.loaded) / p.rate)
  return secs > 90 ? `~${Math.round(secs / 60)} min left` : `~${Math.ceil(secs)}s left`
}

/** Parse-stat chips strip. */
function StatChips({ stats }: { stats: ParseStats }) {
  const chips: string[] = []
  if (stats.kind === 'traces_26as') {
    if (stats.pan) chips.push(`PAN ${stats.pan}`)
    if (stats.txn_count !== undefined) chips.push(`${fmtInt(stats.txn_count)} transactions`)
    if (stats.deductor_blocks !== undefined) chips.push(`${fmtInt(stats.deductor_blocks)} deductors`)
    if (stats.tds_total) chips.push(`TDS ₹${Number(stats.tds_total).toLocaleString('en-IN')}`)
  } else {
    if (stats.gl_name) chips.push(stats.gl_name)
    if (stats.period) chips.push(stats.period)
    if (stats.row_count !== undefined) chips.push(`${fmtInt(stats.row_count)} rows`)
    if (stats.debit_total) chips.push(`Dr ₹${Number(stats.debit_total).toLocaleString('en-IN')}`)
    if (stats.credit_total) chips.push(`Cr ₹${Number(stats.credit_total).toLocaleString('en-IN')}`)
  }
  if (!chips.length) return null
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <span key={c} className="rounded border border-border px-1.5 py-0.5 text-xs">
          {c}
        </span>
      ))}
    </div>
  )
}

function Bar({ value, animated = false }: { value: number; animated?: boolean }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded bg-muted">
      <div
        className={`h-full rounded bg-primary ${animated ? 'animate-pulse' : ''}`}
        style={{ width: `${Math.min(100, value)}%` }}
      />
    </div>
  )
}

function UploadCard({ periodId, clientId, kind, uploads, canUpload, opened, cue }: {
  periodId: string
  clientId: string | null
  kind: 'source' | 'book'
  uploads: Upload[]
  canUpload: boolean
  opened: boolean
  /** "uploading into FY 2025-26 — tolerance ₹500", on the drop target itself:
   *  both live wrong-workspace drops happened for want of exactly this line */
  cue: string | null
}) {
  const meta = KIND_META[kind]
  const queryClient = useQueryClient()
  const { open } = useWorkspaceSession()
  const [phase, setPhase] = useState<Phase>('idle')
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  const [result, setResult] = useState<Upload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mapOpen, setMapOpen] = useState(false)
  // encrypted-file flow: keep the picked file so a password retry can resend
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pwdMessage, setPwdMessage] = useState<string | null>(null)
  const [pwd, setPwd] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)
  // Blank launch, per SIDE. A stored file shows only once THIS side was loaded
  // this session (see workspaceSession for the incident).
  const shown = activeUpload(uploads, kind, opened, result)

  const doUpload = async (file: File, password?: string) => {
    setError(null)
    setResult(null)
    setPhase('transferring')
    try {
      const up = await uploadWithProgress(
        periodId,
        kind,
        file,
        (p) => {
          setProgress(p)
          if (p.fraction >= 1) setPhase('parsing')
        },
        password,
      )
      setPendingFile(null)
      setPwdMessage(null)
      setPwd('')
      setResult(up)
      setPhase('idle')
      setProgress(null)
      open(periodId, kind) // THIS side now has data in the session
      // RESET (not just invalidate): a superseded file's rows must never
      // flash from cache before the fresh data arrives (data hygiene)
      void queryClient.resetQueries({ queryKey: ['raw-summary', periodId] })
      void queryClient.resetQueries({ queryKey: ['upload-rows'] })
      void queryClient.invalidateQueries({ queryKey: ['uploads', periodId] })
      void queryClient.invalidateQueries({ queryKey: ['period-summary', periodId] })
      showToast({
        color: up.parse_status === 'parsed' ? 'green' : 'red',
        title: up.parse_status === 'parsed' ? 'File processed' : 'Parse failed',
        // the sha-dedupe answers with the STORED row whatever the dropped
        // name was — say so, or "I uploaded X and Y appeared" reads as a bug
        message: dedupeNote(file.name, up.filename) ?? up.filename,
      })
    } catch (e) {
      setPhase('idle')
      setProgress(null)
      const msg = String(e instanceof Error ? e.message : e)
      if (msg.startsWith('needs_password:') || msg.includes('Wrong password')) {
        // encrypted file: keep it and ask for the password inline
        setPendingFile(file)
        setPwdMessage(msg.replace(/^needs_password:\s*/, ''))
        setPwd('')
      } else {
        setError(msg)
      }
    }
  }

  return (
    <div className="rounded-md border-2 border-dashed border-border bg-muted/30 p-4">
      <div className="mb-1 flex items-center gap-2">
        <FileSpreadsheet size={22} className="text-primary" />
        <div>
          <p className="text-sm font-bold">{meta.title}</p>
          <p className="text-xs text-muted-foreground">{meta.hint}</p>
        </div>
      </div>

      {canUpload && phase === 'idle' && (
        /* A parsed file collapses the drop target to one thin line: the big
           empty rectangle earned its size while it was the only way in;
           dropping or clicking still replaces the file — the affordance
           shrinks, not the capability. */
        <button
          type="button"
          className={`mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-muted-foreground hover:bg-muted ${
            shown ? 'py-1 text-xs' : 'py-5 text-sm'
          }`}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            const f = e.dataTransfer.files?.[0]
            if (f) void doUpload(f)
          }}
        >
          <UploadIcon size={shown ? 13 : 16} />
          {shown ? 'Replace — drop a new file or click' : 'Drop the file here or click to browse'}
        </button>
      )}
      <input
        ref={fileInput}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void doUpload(f)
          e.target.value = ''
        }}
      />
      {cue && canUpload && (
        <p className="mt-1 text-center text-xs text-muted-foreground">{cue}</p>
      )}

      {phase !== 'idle' && (
        <div className="mt-2 space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">
              {phase === 'transferring' ? 'Uploading…' : 'Parsing on server…'}
            </span>
            <span className="text-xs text-muted-foreground">
              {phase === 'transferring' && progress
                ? `${fmtBytes(progress.loaded)} / ${fmtBytes(progress.total)} ${etaText(progress)}`
                : 'validating & storing rows'}
            </span>
          </div>
          <Bar
            value={phase === 'parsing' ? 100 : (progress?.fraction ?? 0) * 100}
            animated={phase === 'parsing'}
          />
        </div>
      )}

      {error && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
          <X size={14} className="mt-0.5 shrink-0" /> {error}
        </p>
      )}

      {pendingFile && pwdMessage && phase === 'idle' && (
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900">
          <p className="mb-1 flex items-center gap-1.5 font-semibold">
            <Lock size={14} /> {pendingFile.name} is password-protected
          </p>
          <p className="mb-1.5">
            {pwdMessage} The password is used only to open the file — it is never stored.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="password"
              className="w-52 rounded-md border border-border bg-background px-2 py-1 text-xs"
              placeholder="File password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pwd) void doUpload(pendingFile, pwd)
              }}
            />
            <Btn size="xs" kind="primary" disabled={!pwd}
              onClick={() => void doUpload(pendingFile, pwd)}>
              Unlock &amp; upload
            </Btn>
            <Btn size="xs" kind="subtle"
              onClick={() => {
                setPendingFile(null)
                setPwdMessage(null)
              }}>
              Cancel
            </Btn>
          </div>
        </div>
      )}

      {shown && phase === 'idle' && !error && (
        <div
          className={`mt-2 rounded-md border p-2.5 text-xs ${
            uploadTone(shown.parse_status, shown.parse_stats) === 'green'
              ? 'border-emerald-300 bg-emerald-50'
              : uploadTone(shown.parse_status, shown.parse_stats) === 'yellow'
                ? 'border-amber-300 bg-amber-50'
                : 'border-red-300 bg-red-50'
          }`}
        >
          <p className="flex items-center gap-1.5 break-all text-sm font-semibold">
            {shown.parse_status === 'parsed' ? <Check size={15} /> : <X size={15} />}
            {shown.filename}{' '}
            <span className="text-xs font-normal text-muted-foreground">
              ({fmtBytes(shown.size)})
            </span>
          </p>
          {shown.parse_status === 'parsed' ? (
            <>
              {probeFailureSentence(shown.parse_stats) && (
                <p className="mt-1 font-semibold">{probeFailureSentence(shown.parse_stats)}</p>
              )}
              <StatChips stats={shown.parse_stats ?? {}} />
              <CarriedCorrectionsNote note={shown.parse_stats?.carried_corrections} />
              <CarriedVerdictsNote note={shown.parse_stats?.carried_verdicts} />
              {/* nine-point round N6: a books export with no posting dates
                  anywhere is almost certainly party sums — ask ONCE, and the
                  answer stamps the upload for every run that pins it */}
              {kind === 'book' && shown.parse_stats?.grain_suspect === 'party'
                && !shown.parse_stats?.grain && canUpload && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="font-semibold text-orange-800">
                    No posting dates anywhere — party-level sums, not transactions?
                  </span>
                  {([['party', 'Party sums — reconcile at party level'],
                     ['transaction', 'These are transactions']] as const)
                    .map(([g, label]) => (
                      <Btn
                        key={g}
                        size="xs"
                        kind={g === 'party' ? 'warning' : 'default'}
                        onClick={() => {
                          void postJson<{ grain: string }>(
                            `/uploads/${shown.id}/grain`, { grain: g },
                          ).then(() => {
                            setResult({
                              ...shown,
                              parse_stats: { ...shown.parse_stats, grain: g },
                            })
                          })
                        }}
                      >
                        {label}
                      </Btn>
                    ))}
                </div>
              )}
              {shown.parse_stats?.grain === 'party' && (
                <p className="mt-1 font-semibold text-orange-800">
                  Party-level sums confirmed — runs using this file reconcile at party
                  level; transaction matching is unavailable for them.
                </p>
              )}
            </>
          ) : shown.parse_status === 'needs_mapping' ? (
            <>
              <p className="mb-1.5">
                This file's layout wasn't recognised — map its columns once and it will
                parse automatically from then on.
              </p>
              {canUpload && clientId != null && (
                <Btn size="xs" kind="warning" onClick={() => setMapOpen(true)}>
                  Map columns
                </Btn>
              )}
            </>
          ) : (
            <p>{shown.parse_stats?.error ?? 'Parse failed'}</p>
          )}
        </div>
      )}

      {shown && clientId != null && (
        <MappingModal
          upload={shown}
          clientId={clientId}
          opened={mapOpen}
          onClose={() => setMapOpen(false)}
          onParsed={setResult}
        />
      )}
    </div>
  )
}

const TOLERANCE_PRESETS = ['1', '10', '100', '1000']

function toleranceError(v: string): string | null {
  if (v.trim() === '') return null // blank = engine default
  const n = Number(v)
  if (!Number.isFinite(n)) return 'Enter a rupee amount'
  if (n < 0) return 'Tolerance cannot be negative'
  if (n > 10_000_000) return 'Too large (max ₹1,00,00,000)'
  return null
}

export function UploadPage() {
  const { clientId, periodId: pidParam } = useParams()
  const periodId = pidParam ?? ''
  const navigate = useNavigate()
  const { can } = useEvaluation()
  const canRun = can('runs', 'can_create')
  const canUpload = can('uploads', 'can_create')
  const queryClient = useQueryClient()

  const { sides } = useWorkspaceSession()
  const loaded = sides(periodId)
  const summary = usePeriodSummary(periodId)
  const uploads = useUploads(periodId)
  const runs = useRuns(periodId)
  const [confirmDelete, setConfirmDelete] = useState<Upload | null>(null)

  // tolerance: ₹1 unless the period carries a deliberately set default. It used
  // to be seeded from whatever the last run happened to use, so a one-off wide
  // run quietly became the engagement's new normal (Stage 41).
  const [tolerance, setTolerance] = useState<string | null>(null)
  const remembered = summary.data?.period.default_tolerance ?? null
  const tolValue = tolerance ?? (remembered ? String(Number(remembered)) : '1')
  const tolError = toleranceError(tolValue)

  // the run started from this page stays here: progress, then a result banner
  const [startedRunId, setStartedRunId] = useState<string | null>(null)
  const startedRun = useRun(startedRunId ?? '')
  const active = startedRun.data
  const isRunning = active?.status === 'queued' || active?.status === 'running'

  // A run this session finished becomes the run every surface opens on.
  // Watched rather than done in onSuccess, because the POST returns 202 with
  // a QUEUED run: adopting it there would point every surface at a run with
  // no rows, and would adopt one that goes on to fail.
  const adopt = adoptableRun(active)
  useEffect(() => {
    if (adopt != null) rememberRun(periodId, adopt)
  }, [adopt, periodId])

  const deleteMut = useMutation({
    mutationFn: (id: string) => api<void>(`/uploads/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmDelete(null)
      void queryClient.invalidateQueries({ queryKey: ['uploads', periodId] })
      void queryClient.invalidateQueries({ queryKey: ['period-summary', periodId] })
      void queryClient.invalidateQueries({ queryKey: ['raw-summary', periodId] })
      showToast({ color: 'green', title: 'Upload deleted' })
    },
    onError: (err) => {
      setConfirmDelete(null)
      showToast({ color: 'red', title: 'Cannot delete', message: String(err) })
    },
  })

  const parsedOf = (kind: 'source' | 'book') =>
    (uploads.data ?? []).filter(
      (u) => u.kind === kind && u.parse_status === 'parsed'
        && ['traces_26as', 'statement', 'gl_ledger', 'ledger'].includes(u.parse_stats?.kind ?? ''),
    ).filter((u) => (kind === 'source'
      ? ['traces_26as', 'statement']
      : ['gl_ledger', 'ledger']).includes(u.parse_stats?.kind ?? ''))
  // run against the newest parsed file per side (one active file per kind)
  const chosenSource = parsedOf('source')[0]?.id ?? null
  const chosenBook = parsedOf('book')[0]?.id ?? null

  const startRun = useMutation({
    mutationFn: () =>
      postJson<Run>(`/periods/${periodId}/runs`, {
        source_upload_id: chosenSource,
        book_upload_id: chosenBook,
        tolerance: tolValue.trim() === '' ? null : tolValue.trim(),
      }),
    onSuccess: (run) => {
      // stay on this page: progress renders below, then the result banner
      setStartedRunId(run.id)
      void queryClient.invalidateQueries({ queryKey: ['runs', periodId] })
      void queryClient.invalidateQueries({ queryKey: ['period-summary', periodId] })
    },
    onError: (err) => {
      showToast({ color: 'red', title: 'Could not start run', message: String(err) })
    },
  })

  if (uploads.isPending) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
  }

  const history = uploads.data ?? []
  const cue = periodCue(summary.data?.period.label, summary.data?.period.default_tolerance)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <UploadCard
          periodId={periodId}
          clientId={summary.data?.client.id ?? null}
          kind="source"
          uploads={history}
          canUpload={canUpload}
          opened={loaded.source}
          cue={cue}
        />
        <UploadCard
          periodId={periodId}
          clientId={summary.data?.client.id ?? null}
          kind="book"
          uploads={history}
          canUpload={canUpload}
          opened={loaded.book}
          cue={cue}
        />
      </div>

      <div className="rounded-md border border-border p-3">
        {canRun ? (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <label
              className="flex items-center gap-2 text-sm font-semibold"
              htmlFor="tolerance-input"
              title="Differences up to this amount are accepted — and flagged as within tolerance"
            >
              Tolerance (₹):
              <input
                id="tolerance-input"
                list="tolerance-presets"
                className={`w-32 rounded-md border bg-background px-2 py-1.5 text-sm font-normal ${
                  tolError ? 'border-red-400' : 'border-border'
                }`}
                value={tolValue}
                onChange={(e) => setTolerance(e.target.value)}
                aria-label="Tolerance in rupees"
              />
              <datalist id="tolerance-presets">
                {TOLERANCE_PRESETS.map((p) => <option key={p} value={p} />)}
              </datalist>
            </label>
            <Btn
              kind="primary"
              size="md"
              disabled={!chosenSource || !chosenBook || tolError !== null || isRunning}
              loading={startRun.isPending || isRunning}
              onClick={() => startRun.mutate()}
            >
              Process &amp; Reconcile
            </Btn>
            {tolError && <span className="text-xs text-red-700">{tolError}</span>}
          </div>
        ) : (
          <p className="rounded-md border border-blue-200 bg-blue-50 p-2 text-center text-sm text-blue-900">
            Reviewers have read-only access — a preparer or admin starts runs.
          </p>
        )}

        {active && isRunning && (
          <div className="mt-4">
            <ProcessingPanel run={active} lastDurations={summary.data?.last_step_durations} />
          </div>
        )}

        {active && active.status === 'complete' && active.control_totals && (
          <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm">
            <p className="mb-2 flex items-center gap-1.5 font-semibold text-emerald-900">
              <Check size={18} />
              {active.control_totals.ties_ok
                ? 'TIES OK — reconciliation complete'
                : 'Completed'}
            </p>
            <div className="mb-2 flex flex-wrap items-center gap-4">
              <span>
                Gap <Money v={active.control_totals.gap} bold />
              </span>
              {Object.entries(active.control_totals.categories).map(([cat, v]) => (
                <span key={cat} className="text-muted-foreground">
                  {cat.split(' ')[0]}: <b>{v.n}</b>
                </span>
              ))}
              {active.tolerance && (
                <Chip tone="yellow">
                  tolerance ₹{Number(active.tolerance).toLocaleString('en-IN')}
                </Chip>
              )}
            </div>
            <Btn
              size="xs"
              kind="primary"
              onClick={() =>
                void navigate(`/w/${clientId}/${periodId}/reconciliation?run=${active.id}`)
              }
            >
              View reconciliation <ArrowRight size={14} />
            </Btn>
          </div>
        )}

        {active && (active.status === 'failed' || active.status === 'failed_validation') && (
          <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm">
            <p className="mb-1 flex items-center gap-1.5 font-semibold text-red-900">
              <X size={18} /> Run failed
            </p>
            <p className="whitespace-pre-wrap text-xs">{active.error}</p>
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="rounded-md border border-border p-3">
          <div className="mb-2 flex items-baseline gap-2">
            <h2 className="text-sm font-semibold">Upload history</h2>
            <span className="text-xs text-muted-foreground">{history.length}</span>
            <InfoTip>
              Duplicate uploads (same content) are deduplicated per period. Files used by
              a completed run cannot be deleted — they are frozen provenance.
            </InfoTip>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-2">File</th>
                  <th className="py-1.5 pr-2">Category</th>
                  <th className="py-1.5 pr-2">Status</th>
                  <th className="py-1.5 pr-2">Size</th>
                  <th className="py-1.5 pr-2">Uploaded</th>
                  <th className="py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((u) => (
                  <tr key={u.id} className="border-b border-border/60">
                    <td className="break-all py-1.5 pr-2">
                      <span className="flex items-center gap-1.5">
                        <FileSpreadsheet size={15} className="shrink-0 text-emerald-700" />
                        {u.filename}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Chip>
                        {u.kind === 'source' ? '26AS' : u.kind === 'book' ? 'Books' : 'Evidence'}
                      </Chip>
                    </td>
                    <td className="py-1.5 pr-2">
                      <StatusBadge status={u.parse_status} />
                    </td>
                    <td className="py-1.5 pr-2">{fmtBytes(u.size)}</td>
                    <td className="py-1.5 pr-2">{fmtDateTime(u.uploaded_at)}</td>
                    <td className="py-1.5">
                      <span className="flex gap-1">
                        <button
                          className="rounded p-1 hover:bg-muted"
                          aria-label="Download"
                          onClick={() =>
                            // the presign flow: ask for the short-lived URL,
                            // then let the browser save the file
                            void api<{ url: string; filename: string }>(
                              `/uploads/${u.id}/download`,
                            ).then(async ({ url, filename }) => {
                              const res = await fetch(url)
                              const blob = await res.blob()
                              const a = document.createElement('a')
                              a.href = URL.createObjectURL(blob)
                              a.download = filename
                              a.click()
                              URL.revokeObjectURL(a.href)
                            }).catch((e) =>
                              showToast({
                                color: 'red',
                                title: 'Download failed',
                                message: String(e),
                              }),
                            )
                          }
                        >
                          <Download size={15} />
                        </button>
                        {canUpload && (
                          <button
                            className="rounded p-1 text-red-700 hover:bg-red-50"
                            aria-label="Delete"
                            onClick={() => setConfirmDelete(u)}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-md border border-border p-3">
        <h2 className="mb-2 text-sm font-semibold">Runs</h2>
        {(runs.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No runs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-2">Run</th>
                  <th className="py-1.5 pr-2">Status</th>
                  <th className="py-1.5 pr-2">Started</th>
                  <th className="py-1.5 pr-2">Finished</th>
                  <th className="py-1.5 pr-2 text-right">Gap</th>
                  <th className="py-1.5">Tie-out</th>
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).map((r) => (
                  <tr key={r.id} className="border-b border-border/60">
                    <td className="py-1.5 pr-2 font-mono text-xs font-semibold">{runTag(r.id)}</td>
                    <td className="py-1.5 pr-2"><StatusBadge status={r.status} /></td>
                    <td className="py-1.5 pr-2">{fmtDateTime(r.created_at)}</td>
                    <td className="py-1.5 pr-2">{fmtDateTime(r.finished_at)}</td>
                    <td className="py-1.5 pr-2 text-right">
                      {r.control_totals ? <Money v={r.control_totals.gap} /> : '—'}
                    </td>
                    <td className="py-1.5">
                      {r.control_totals ? (
                        <Chip tone={r.control_totals.ties_ok ? 'green' : 'red'}>
                          {r.control_totals.ties_ok ? 'TIES OK' : 'FAILED'}
                        </Chip>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        opened={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete upload?"
      >
        <p className="mb-4 text-sm">
          Delete <b>{confirmDelete?.filename}</b> and its parsed rows? Files referenced by a
          run cannot be deleted.
        </p>
        <div className="flex justify-end gap-2">
          <Btn onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn
            kind="danger"
            loading={deleteMut.isPending}
            onClick={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
          >
            Delete
          </Btn>
        </div>
      </Modal>
    </div>
  )
}
